<template>
  <!-- Custom text styles of the CI (spec 2026-10-15 §6): a list in the Brand Kit's
       typography tile, below the roles. A click on a line opens its fields below,
       like the role list above. The parent owns saving and listens to `change`. -->
  <div class="custom-text-styles" data-test="custom-text-styles">
    <b-row class="mb-3">
      <b-col cols="12" offset-sm="1" sm="3">
        <label>{{ t('title') }}</label>
      </b-col>
      <b-col cols="12" sm="7">
        <b-form-text>{{ t('hint') }}</b-form-text>
      </b-col>
    </b-row>
    <b-row class="mb-4">
      <b-col cols="12">
        <div class="cts-layout">
          <div v-if="styles.length" class="cts-list" data-test="cts-list">
            <button
              v-for="s in styles"
              :key="s.id"
              type="button"
              class="cts-row"
              :class="{ active: s.id === selectedId }"
              :aria-pressed="String(s.id === selectedId)"
              :data-test="`cts-row-${s.id}`"
              @click="selectedId = s.id"
            >
              <span class="cts-row-sample" :style="preview(s, 24)">{{ s.name }}</span>
              <span class="cts-row-values">{{ values(s) }}</span>
            </button>
          </div>

          <form class="cts-add" data-test="cts-add" @submit.prevent="add">
            <b-form-input
              v-model="newName"
              size="sm"
              maxlength="40"
              data-test="cts-new-name"
              :disabled="full"
              :placeholder="t('namePlaceholder')"
            />
            <b-button type="submit" size="sm" variant="outline-light" data-test="cts-add-button" :disabled="full || !newName.trim()">
              {{ t('add') }}
            </b-button>
          </form>
          <div v-if="full" class="cts-note">{{ t('full') }}</div>

          <div v-if="selected" :key="`${selected.id}-${renderKey}`" class="cts-inspector" :data-test="`cts-inspector-${selected.id}`">
            <div>
              <div class="cts-small">{{ t('selected') }}</div>
              <div class="cts-sample" :style="preview(selected, 48)">{{ t('sample') }}</div>
            </div>

            <div class="cts-field">
              <label :for="id('name')">{{ t('name') }}</label>
              <b-form-input :id="id('name')" maxlength="40" data-test="cts-name" :value="selected.name" @change="rename" />
            </div>

            <div class="cts-field">
              <label :for="id('font')">{{ t('font') }}</label>
              <b-form-select :id="id('font')" data-test="cts-font" :value="selected.font || 'text'" :options="fontOptions" @change="setFont" />
            </div>

            <div class="cts-pair">
              <div v-for="field in ['min', 'max']" :key="field" class="cts-field">
                <label :for="id(field)">{{ t(field === 'min' ? 'sizeMobile' : 'sizeDesktop') }}</label>
                <b-form-input
                  :id="id(field)"
                  type="text"
                  inputmode="decimal"
                  :data-test="`cts-${field}`"
                  :value="groesseAnzeigen(selected[field])"
                  @change="setSize(field, $event)"
                />
              </div>
              <div class="cts-field">
                <label :for="id('lh')">{{ typoT('feld.zeilenhoehe') }}</label>
                <b-form-input
                  :id="id('lh')"
                  type="text"
                  inputmode="decimal"
                  data-test="cts-lh"
                  :placeholder="t('inherit')"
                  :value="numberText(selected.lineHeight)"
                  @change="setLineHeight"
                />
              </div>
              <div class="cts-field">
                <label :for="id('weight')">{{ typoT('stil.staerke') }}</label>
                <b-form-select :id="id('weight')" data-test="cts-weight" :value="selected.weight || ''" :options="weightOptions" @change="setWeight" />
              </div>
              <div v-for="field in ['italic', 'uppercase']" :key="field" class="cts-field">
                <label :for="id(field)">{{ t(field) }}</label>
                <b-form-select
                  :id="id(field)"
                  :data-test="`cts-${field}`"
                  :value="yesNoValue(selected[field])"
                  :options="yesNoOptions"
                  @change="setYesNo(field, $event)"
                />
              </div>
              <div class="cts-field">
                <label :for="id('ls')">{{ typoT('feld.laufweite') }}</label>
                <b-form-input
                  :id="id('ls')"
                  type="text"
                  inputmode="decimal"
                  data-test="cts-ls"
                  :placeholder="t('inherit')"
                  :title="typoT('laufweiteHint')"
                  :value="numberText(selected.ls)"
                  @change="setLetterSpacing"
                />
              </div>
              <div class="cts-field">
                <label :for="id('color')">{{ t('color') }}</label>
                <div class="cts-color">
                  <input
                    :id="id('color')"
                    type="color"
                    class="cts-swatch"
                    data-test="cts-color"
                    :value="color ? color.hex : '#000000'"
                    @change="setColor($event.target.value, color ? color.alpha : 100)"
                  >
                  <template v-if="color">
                    <b-form-input
                      type="number"
                      min="0"
                      max="100"
                      size="sm"
                      class="cts-alpha"
                      data-test="cts-alpha"
                      :title="t('opacity')"
                      :value="color.alpha"
                      @change="setColor(color.hex, $event)"
                    />
                    <span class="cts-unit">%</span>
                    <button type="button" class="cts-link cts-link-quiet" data-test="cts-color-clear" @click="setField('color', null)">
                      {{ t('noColor') }}
                    </button>
                  </template>
                  <span v-else class="cts-unit">{{ t('inherit') }}</span>
                </div>
              </div>
            </div>

            <div class="cts-bar">
              <div>
                <button type="button" class="cts-link" data-test="cts-up" :disabled="selectedIndex === 0" @click="move(-1)">{{ t('up') }}</button>
                <button type="button" class="cts-link" data-test="cts-down" :disabled="selectedIndex === styles.length - 1" @click="move(1)">{{ t('down') }}</button>
              </div>
              <button type="button" class="cts-link cts-link-danger" data-test="cts-delete" @click="remove">{{ t('delete') }}</button>
            </div>
          </div>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { TYPE_WEIGHTS } from '@/editor-v2/utils/ciTokens';
import {
  groesseAnzeigen, groesseLesen, zeilenhoeheLesen, laufweiteLesen,
} from './typoTabelle';
import {
  CUSTOM_STYLES_MAX, addCustomStyle, removeCustomStyle, moveCustomStyle, setCustomStyleField, colorParts, colorFrom, previewStyle,
} from './customTextStyles';

export default {
  name: 'CustomTextStylesEditor',
  props: {
    // designTokens.typography of the CI; edited in place.
    typo: { type: Object, required: true },
    // [{value, text}] or plain names: the CI's fonts.
    fonts: { type: Array, default: () => [] },
  },
  data: () => ({
    selectedId: null,
    newName: '',
    // Bumped on invalid input so one-way :value fields re-render the stored value.
    renderKey: 0,
  }),
  computed: {
    styles() {
      return Array.isArray(this.typo.customStyles) ? this.typo.customStyles : [];
    },
    selectedIndex() {
      return this.styles.findIndex((s) => s.id === this.selectedId);
    },
    selected() {
      return this.styles[this.selectedIndex] || null;
    },
    full() {
      return this.styles.length >= CUSTOM_STYLES_MAX;
    },
    color() {
      return this.selected ? colorParts(this.selected.color) : null;
    },
    fontOptions() {
      const list = this.fonts.filter(Boolean).map((o) => (typeof o === 'string' ? { value: o, text: o } : o))
        .filter((o) => o.value && o.value !== 'display' && o.value !== 'text');
      return [{ value: 'display', text: this.t('fontDisplay') }, { value: 'text', text: this.t('fontText') }, ...list];
    },
    weightOptions() {
      return [{ value: '', text: this.t('inherit') }, ...TYPE_WEIGHTS.map((w) => ({ value: w, text: `${w} ${this.typoT(`staerkeName.${w}`)}` }))];
    },
    yesNoOptions() {
      return [
        { value: '', text: this.t('inherit') },
        { value: 'yes', text: this.typoT('stil.ja') },
        { value: 'no', text: this.typoT('stil.nein') },
      ];
    },
  },
  methods: {
    groesseAnzeigen,
    t(key, values) {
      return this.$t(`settings.ciBrandKit.customStyles.${key}`, values);
    },
    typoT(key) {
      return this.$t(`settings.project.corporateIdentity.typo.${key}`);
    },
    id(field) {
      return `cts-${this._uid}-${field}`;
    },
    preview(style, maxPx) {
      return previewStyle(style, this.typo, maxPx);
    },
    values(style) {
      const size = `${groesseAnzeigen(style.min)} / ${groesseAnzeigen(style.max)} px`;
      return style.weight ? `${size} · ${style.weight}` : size;
    },
    numberText(v) {
      return typeof v === 'number' ? String(v) : '';
    },
    yesNoValue(v) {
      if (v === true) return 'yes';
      if (v === false) return 'no';
      return '';
    },
    changed() {
      this.$emit('change');
    },
    setField(field, value) {
      setCustomStyleField(this.selected, field, value);
      this.changed();
    },
    add() {
      const style = addCustomStyle(this.typo, this.newName);
      if (!style) return;
      this.newName = '';
      this.selectedId = style.id;
      this.changed();
    },
    rename(text) {
      const name = String(text).trim().slice(0, 40);
      if (!name) {
        this.renderKey += 1;
        return;
      }
      this.setField('name', name);
    },
    setFont(font) {
      this.setField('font', font);
      if (font !== 'display' && font !== 'text') this.$emit('font', font);
    },
    // Sizes are required: an empty or invalid field shows the stored value again.
    setSize(field, text) {
      const rem = groesseLesen(text, 'px');
      if (typeof rem !== 'number') {
        this.renderKey += 1;
        return;
      }
      this.setField(field, rem);
    },
    // Optional values: empty removes, invalid shows the stored value again.
    setOptional(field, value) {
      if (value === undefined) {
        this.renderKey += 1;
        return;
      }
      this.setField(field, value);
    },
    setLineHeight(text) {
      this.setOptional('lineHeight', zeilenhoeheLesen(text, this.selected.max || 1));
    },
    setLetterSpacing(text) {
      this.setOptional('ls', laufweiteLesen(text));
    },
    setWeight(w) {
      this.setField('weight', TYPE_WEIGHTS.includes(Number(w)) ? Number(w) : null);
      // A new weight needs its cut loaded.
      if (!w) return;
      const { font } = this.selected;
      const name = font === 'display' || font === 'text' ? this.typo[`${font}Font`] : font;
      if (name) this.$emit('font', String(name).replace(/^'(.*)'$/, '$1'));
    },
    setYesNo(field, choice) {
      this.setField(field, choice === '' ? null : choice === 'yes');
    },
    setColor(hex, alpha) {
      this.setField('color', colorFrom(hex, alpha === '' ? 100 : alpha));
    },
    move(delta) {
      moveCustomStyle(this.typo, this.selectedIndex, delta);
      this.changed();
    },
    async remove() {
      const ok = await this.$bvModal.msgBoxConfirm(this.t('deleteText'), {
        title: this.t('deleteTitle', { name: this.selected.name }),
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: this.t('delete'),
        cancelTitle: this.$t('buttons.cancel'),
        footerClass: 'flex-nowrap',
        centered: true,
      });
      if (!ok || !this.selected) return;
      removeCustomStyle(this.typo, this.selectedIndex);
      this.selectedId = null;
      this.changed();
    },
  },
};
</script>

<style scoped>
/* Same frame as TypoVorschau's list mode: list, then the chosen style's fields. */
.custom-text-styles {
  width: 0; min-width: calc(100% + 30px); margin: 0 -15px; padding: 0 15px; box-sizing: border-box;
}
.cts-layout { display: flex; flex-direction: column; gap: 12px; }
.cts-list { display: flex; flex-direction: column; gap: 2px; }
.cts-row {
  all: unset; box-sizing: border-box; cursor: pointer; display: flex; justify-content: space-between;
  align-items: baseline; gap: 12px; padding: 8px 10px; border-radius: 6px;
}
.cts-row:hover { background: #4a4e52; }
.cts-row.active { background: #5b6064; }
.cts-row:focus-visible { outline: 2px solid #51d182; }
.cts-row-sample { color: #fff; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cts-row-values { flex-shrink: 0; font-size: 13px; color: #b9bbbf; }

.cts-add { display: flex; gap: 8px; }
.cts-add .form-control { max-width: 280px; }
.cts-note { font-size: 13px; color: #b9bbbf; }

.cts-inspector {
  box-sizing: border-box; padding: 24px; background: #53575b; border-radius: 12px;
  display: flex; flex-direction: column; gap: 18px;
}
.cts-small { font-size: 12px; letter-spacing: 1px; font-weight: 700; text-transform: uppercase; color: #b9bbbf; }
/* A dark tile: styles like the EVZ number are light on a photo and would vanish on the grey panel. */
.cts-sample {
  color: #fff; margin-top: 8px; padding: 12px 16px; background: #2f3236; border-radius: 8px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.cts-field { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
.cts-field label { margin: 0; }
.cts-pair { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
.cts-color { display: flex; align-items: center; gap: 8px; min-height: 38px; }
.cts-swatch { width: 38px; height: 38px; padding: 2px; border: 1px solid #6b7075; border-radius: 6px; background: transparent; cursor: pointer; }
.cts-alpha { width: 72px; }
.cts-unit { font-size: 13px; color: #b9bbbf; }
.cts-bar { display: flex; justify-content: space-between; gap: 16px; border-top: 1px solid #5b6064; padding-top: 16px; }
.cts-link { all: unset; cursor: pointer; color: #51d182; font-size: 14px; margin-right: 12px; }
.cts-link:hover, .cts-link:focus-visible { text-decoration: underline; }
.cts-link:disabled { color: #8a8d92; cursor: default; text-decoration: none; }
.cts-link-quiet { color: #d9dadc; margin-right: 0; }
.cts-link-danger { color: #f08b8b; margin-right: 0; }
</style>
